import React, {
  memo, useEffect, useMemo, useRef, useState,
} from 'react'

import ReactTooltip from 'react-tooltip'

import {
  Icon, Button, Close, MoreFilters as MoreFiltersIcon, Search,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, func, string } from 'prop-types'
import { createPortal } from 'react-dom'

import DatePublishedContent from 'Pods/SearchPage/Components/MainFilters/Components/DatePublished/DatePublishedContent'
import DesignerContent from 'Pods/SearchPage/Components/MainFilters/Components/Designers/DesignerContent'
import FileFormatContent from 'Pods/SearchPage/Components/MainFilters/Components/FileFormat/FileFormatContent'
import LicenseContent from 'Pods/SearchPage/Components/MainFilters/Components/License/LicenseContent'
import PriceContent from 'Pods/SearchPage/Components/MainFilters/Components/Price/PriceContent'
import QualityContent from 'Pods/SearchPage/Components/MainFilters/Components/Quality/QualityContent'
import I18n from 'Pods/Shared/I18n'

import AdvancedFiltersContent from './AdvancedFiltersContent'
import CollapsibleSection from './CollapsibleSection'

const translate = (key) => I18n.t(key, { ns: 'rails' })

const headerIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />
const closeIcon = <Icon notSpaced svgComponent={Close} />

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

// Modal starts where the fixed app navbar ends; measured on mount so it adapts to the
// sales-off bar and breakpoint-specific navbar heights.
const getNavbarOffset = () => {
  const nav = typeof document !== 'undefined' && document.querySelector('.wayfinder')
  return nav ? Math.round(nav.getBoundingClientRect().bottom) : 0
}

function MoreFiltersDrawer({ onClose, onReset, fileTypes, onFileTypeChange }) {
  const drawerRef = useRef(null)
  const [navOffset] = useState(getNavbarOffset)

  const searchTextInputProps = useMemo(() => ({
    leadIcon: <Icon notSpaced svgComponent={Search} color="dark" />,
    textInputClassName: 'filter-wrapper__search-input',
  }), [])

  // Lock background page scroll while open, reusing the app-wide body class
  // (see ModelGallery, GalleryArea, the cart popover).
  useEffect(() => {
    document.body.classList.add('disable-page-scroll')
    return () => document.body.classList.remove('disable-page-scroll')
  }, [])

  // Close when the main mobile menu opens. The hamburger lives in the navigation pod, so
  // listen for its click at the document level rather than coupling to that pod.
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (event.target.closest?.('.hamburger')) {
        onClose()
      }
    }
    document.addEventListener('click', handleDocumentClick)

    return () => document.removeEventListener('click', handleDocumentClick)
  }, [onClose])

  // Modal a11y: focus the drawer on open, trap Tab inside it, close on Escape, and restore
  // focus to the opener on close.
  useEffect(() => {
    const drawer = drawerRef.current
    const previouslyFocused = document.activeElement
    const getFocusable = () => (drawer ? Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)) : [])

    const focusable = getFocusable()
    if (focusable.length > 0) {
      focusable[0].focus()
    } else if (drawer) {
      drawer.focus()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = getFocusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [onClose])

  // react-tooltip only auto-hides on window scroll; the drawer is its own scroll container,
  // so dismiss any open tooltip when its content scrolls.
  const handleScroll = () => {
    ReactTooltip.hide()
  }

  return createPortal(
    // Portaled to body so the full-screen modal escapes the filter bar's stacking context
    // (.search-page__main-filters { z-index: 2 }). It starts below the fixed navbar via the
    // measured offset, leaving the navbar visible.
    <div
      ref={drawerRef}
      className="more-filters__drawer"
      role="dialog"
      aria-modal="true"
      aria-label={translate('search_page.filters.more_filters.modal_title')}
      tabIndex={-1}
      style={{ top: navOffset }}
    >
      {/* Header + sections scroll together; the footer stays pinned at the bottom (see SCSS). */}
      <div className="more-filters__drawer-scroll" onScroll={handleScroll}>
        <div className="more-filters__drawer-header">
          <div className="more-filters__drawer-title">
            {headerIcon}
            <span>{translate('search_page.filters.more_filters.modal_title')}</span>
          </div>
          <Button
            type="ghost"
            variant="only-icon"
            icon={closeIcon}
            onClick={onClose}
            aria-label={translate('search_page.filters.more_filters.close_label')}
          />
        </div>
        <CollapsibleSection title={translate('search_page.filters.file_format.title')}>
          <FileFormatContent
            fileTypes={fileTypes}
            onFilterChange={onFileTypeChange}
            textInputProps={searchTextInputProps}
            limit={3}
            hideShowMore={false}
          />
        </CollapsibleSection>
        <CollapsibleSection title={translate('search_page.filters.price.title')}>
          <PriceContent />
        </CollapsibleSection>
        <CollapsibleSection title={translate('search_page.filters.license.title')}>
          <LicenseContent hideGroupLabels />
        </CollapsibleSection>
        <CollapsibleSection title={translate('search_page.filters.date_published.title')}>
          <DatePublishedContent limit={3} hideShowMore={false} />
        </CollapsibleSection>
        {/* Tooltip copy comes from the Figma design and is pending review with designers
            (e.g. lowercase "ai"); see the note in RailsEn.js next to the key. */}
        <CollapsibleSection
          title={translate('search_page.filters.quality.title')}
          tooltip={translate('search_page.filters.quality.tooltip')}
        >
          {/* The collapsible header is the "Quality" title, so hide the group's own label. */}
          <QualityContent hideQualityLabel />
        </CollapsibleSection>
        <CollapsibleSection title={translate('search_page.filters.designers.title')}>
          <DesignerContent />
        </CollapsibleSection>
        {/* Advanced filters (Advanced / Content safety / Personal / Purchases) share the same
            content component as the desktop More Filters popover; the sub-group labels come from
            the configs, so the collapsible header is the single "Advanced filters" title. */}
        <CollapsibleSection title={translate('search_page.filters.advanced.title')}>
          <AdvancedFiltersContent hideAdvancedLabel />
        </CollapsibleSection>
      </div>
      <div className="more-filters__drawer-footer">
        <Button
          type="secondary"
          customClass="more-filters__footer-button"
          onClick={onReset}
        >
          {translate('search_page.filters.more_filters.reset')}
        </Button>
        <Button
          type="primary"
          customClass="more-filters__footer-button"
          onClick={onClose}
        >
          {translate('search_page.filters.more_filters.apply_filters')}
        </Button>
      </div>
    </div>,
    document.body,
  )
}

MoreFiltersDrawer.propTypes = {
  onClose: func.isRequired,
  onReset: func.isRequired,
  fileTypes: arrayOf(string).isRequired,
  onFileTypeChange: func.isRequired,
}

export default memo(MoreFiltersDrawer)
